// @lemontaigu/platform — BRANDING: the tenant's name and chrome colour, as
// parameters rather than literals (docs/UI-BRANDING.md B4–B6).
//
// WHY THIS IS A PLATFORM CONCERN, not a domain one. Every cockpit renders the
// same header and asks the same two questions — whose name is on it, and what
// colour is it. Answering them per app is how five apps ended up with six
// hard-coded copies of « Le Montaigu » and 144 copies of one hex value.
// Branding sits beside authentication and capabilities for exactly the reason
// those do: one question, one answer, one place to change it.
//
// THE PRINCIPLE, which is the operator's own (2016, « The Case for External
// Parametrization », filed in DesignVault/00-claude-os/references/): parameters
// arrive by INJECTION from a context, and are external to the component's code
// and logic. A component that reads a colour constant is a component naming its
// own configuration — the same failure ARCHITECTURE.md already forbids for
// infrastructure, applied to parameters.
//
// Zero dependencies, like the rest of this package (check E4): the store is a
// port, and the Supabase implementation lives under `/adapters/client`.

/** What a tenant declares about how its apps look and what they are called. */
export interface Branding {
  tenantCode: string;
  /** The organisation name, shown beside the app name. The APP name stays a
   *  compiled constant (B2) — « PMS » is what this software is, and does not
   *  vary by tenant; « Le Montaigu » is data, and does. */
  labelFr: string;
  /** App slug → 6-digit hex. `default` applies to any app with no own entry.
   *  Empty means the tenant states nothing and the caller keeps its fallback. */
  chrome: Readonly<Record<string, string>>;
}

/**
 * DRIVEN PORT — how the UI reaches the branding. One fetch per session, like
 * `CapabilityStore` and for the same reason: this is asked once per header, and
 * a per-question round trip is how the thing it replaces behaved.
 */
export interface BrandingStore {
  /** The caller's tenant branding, or null if the read found no tenant. */
  mine(): Promise<Branding | null>;
}

/**
 * The colour an app should paint its chrome, or `null` for "keep your
 * fallback". Pure, so the resolution rule is testable without a database.
 *
 * `null` rather than a baked-in default ON PURPOSE. A default here would be a
 * third place the brand blue is written down, which is the disease. The single
 * compiled copy lives in the CSS `var(--chrome-navy, #232347)` fallback, where
 * it is also doing a second job — see §6.2 of the branding doc: it is what
 * makes the pre-fetch paint identical to the post-fetch one, so nothing
 * flashes for the tenant that exists.
 */
export function chromeColorFor(branding: Branding | null, app: string): string | null {
  if (!branding) return null;
  return branding.chrome[app] ?? branding.chrome.default ?? null;
}

/**
 * The header string: app name (constant) + organisation name (data), joined
 * the way every shell already writes it by hand.
 *
 * Falls back to the app name ALONE while the tenant is unknown, never to a
 * hard-coded organisation. « PMS » on its own is true; « PMS — Le Montaigu »
 * compiled into a binary served to a second tenant is a lie rendered as fact,
 * and the reason this function exists.
 */
export function brandLabel(app: string, branding: Branding | null): string {
  return branding?.labelFr ? `${app} — ${branding.labelFr}` : app;
}
